import { clsx } from "@trydig/ui";
import { twMerge } from "tailwind-merge";
import { Accordion, type AccordionItem } from "@/components/ui/Accordion";
import { Container } from "@/components/ui/Container";
import { Typography } from "@/components/ui/Typography";
import { isDarkMode } from "@/lib/utils/isDarkMode";

export type AccordionSectionAlignment = "left" | "center" | "right";

export interface AccordionSectionGroup {
	id?: string;
	title?: string;
	items: AccordionItem[];
	/** Spread onto the group wrapper — used by the Storyblok blok to attach storyblokEditable. */
	wrapperProps?: Record<string, unknown>;
}

export interface AccordionSectionProps {
	heading?: string;
	groups?: AccordionSectionGroup[];
	/**
	 * Layout variant.
	 *  - `left`: single column anchored to the left of the section, heading at top.
	 *  - `center` (default): single column centered, heading at top.
	 *  - `right`: split layout — heading on the left half (col-span-5), accordion
	 *    list on the right half (col-span-7). Used on the FAQ item page.
	 */
	alignment?: AccordionSectionAlignment;
	bg?: string;
	className?: string;
}

const SINGLE_COLUMN_ALIGN: Record<Exclude<AccordionSectionAlignment, "right">, string> = {
	left: "items-start",
	center: "items-center",
};

const Heading = ({ heading, darkMode }: { heading: string; darkMode?: boolean }) => (
	<Typography
		variant="heading-display-md"
		as="h2"
		className={clsx("text-text-dark-text-1 uppercase", darkMode && "text-text-light-text-1")}
	>
		{heading}
	</Typography>
);

const Group = ({ group, darkMode }: { group: AccordionSectionGroup; darkMode?: boolean }) => (
	<div
		className="flex w-full flex-col gap-grid-section-vertical-md"
		{...(group.wrapperProps ?? {})}
	>
		{group.title && (
			<Typography
				variant="heading-display-xs"
				as="h3"
				className={clsx("text-text-dark-text-1 uppercase", darkMode && "text-text-light-text-1")}
			>
				{group.title}
			</Typography>
		)}
		<Accordion size="large" items={group.items} darkMode={darkMode} />
	</div>
);

const GroupList = ({
	groups,
	darkMode,
}: {
	groups: AccordionSectionGroup[];
	darkMode?: boolean;
}) => (
	<>
		{groups.map((group, index) => (
			<Group key={group.id ?? index} group={group} darkMode={darkMode} />
		))}
	</>
);

export const AccordionSection = ({
	heading,
	groups,
	alignment = "center",
	bg,
	className,
}: AccordionSectionProps) => {
	if (!groups || groups.length === 0) return null;

	const sectionClasses = twMerge("py-grid-section-vertical-lg", className);

	const darkMode = isDarkMode(bg);

	if (alignment === "right") {
		return (
			<section className={sectionClasses} style={bg ? { backgroundColor: bg } : undefined}>
				<Container className="grid grid-cols-12 gap-grid-grid-gutter-md">
					<div className="col-span-full lg:col-span-5">
						{heading && <Heading heading={heading} darkMode={darkMode} />}
					</div>
					<div className="col-span-full flex flex-col gap-grid-section-vertical-lg lg:col-span-7">
						<GroupList groups={groups} darkMode={darkMode} />
					</div>
				</Container>
			</section>
		);
	}

	return (
		<section className={sectionClasses} style={bg ? { backgroundColor: bg } : undefined}>
			<Container>
				<div className={twMerge("flex flex-col", SINGLE_COLUMN_ALIGN[alignment])}>
					<div className="flex w-full max-w-[40.5rem] flex-col gap-grid-section-vertical-lg">
						{heading && <Heading heading={heading} darkMode={darkMode} />}
						<GroupList groups={groups} darkMode={darkMode} />
					</div>
				</div>
			</Container>
		</section>
	);
};
